import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import CustomizedTabs from "../../../../shared/components/navigations/tabs";
import { useApi } from "../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { mapToTransactionCertificateReqDto } from "../tc-models/tc-req-dto/transaction-certificate.req_dto";
import { useFormStore } from "../../../../store/transaction-certificates.store";
import { useTcTabErrorStore } from "../../../../store/tcTabErrors.store";
import { fetchIncomingTcDetailsList } from "./tc-tabs/incoming-tc-details/incoming-details-apis";
import { fetchProductaddDetails } from "./tc-tabs/certified-products/product-api";

// Tabs
import TransactionCertificate from "./tc-tabs/transaction-cetificate";
import SellerCertifiedProducts from "./tc-tabs/buyer-seller/seller-certfied-products";
import Shipments from "./tc-tabs/shipments/shipments";
import CertifiedProductsComponents from "./tc-tabs/certified-products";
// Component Information is merged into the single "Product Information" tab (TC v4
// product-without-component). The standalone Component tab is intentionally retired.
import TcStandardWeightInfo from "./tc-tabs/tc-standard-weight-info";
import IncomingTcDetails from "./tc-tabs/incoming-tc-details/index";
import DeclarationByCertificateBody from "./tc-tabs/declaration-by-certificate-body";

const TcForm = () => {
  const { formtype, TE_ID, currentTab } = useParams<{
    formtype: string;
    TE_ID: string;
    currentTab: string;
  }>();
  const baseUrl = formtype || "create-tc";
  const navigate = useNavigate();

  const enableDisableLogic: boolean = formtype === "submited-tc" ? false : true;

  const [completionMap, setCompletionMap] = useState<Record<string, boolean>>(
    {}
  );
  const [tcStatus, setTcStatus] = useState<string>("");
  const [isSharedBaseHydrating, setIsSharedBaseHydrating] = useState(
    () => Boolean(TE_ID && currentTab !== "base-details")
  );

  const { executeGet } = useApi();

  // ── Per-tab error flags (RMDF product violations, etc.) ───────────────────
  // Mirrors the scStandard.store / SC v4 facility-details pattern.
  // When a child tab reports an error the parent paints the pill orange.
  const { tabErrors, clearTabErrors } = useTcTabErrorStore();

  useEffect(() => {
    if (!TE_ID || currentTab === "base-details") {
      setIsSharedBaseHydrating(false);
      return;
    }

    const currentBaseDetails =
      useFormStore.getState().getFormValues("Transaction-Details") || {};
    if (
      String(currentBaseDetails.tcId || "") === String(TE_ID) &&
      Array.isArray(currentBaseDetails.tcStandard)
    ) {
      setIsSharedBaseHydrating(false);
      return;
    }

    let cancelled = false;
    setIsSharedBaseHydrating(true);

    const hydrateSharedBaseDetails = async () => {
      try {
        const response: any = await executeGet(
          TCAppConfig.GET_TC_BASE_DETAILS_V4,
          { tcId: TE_ID }
        );
        if (cancelled) return;

        const baseDetails = mapToTransactionCertificateReqDto(
          response.data,
          TE_ID
        );
        Object.entries(baseDetails).forEach(([key, value]) => {
          useFormStore
            .getState()
            .setFormValues("Transaction-Details", { [key]: value });
        });
      } catch (error) {
        console.error("Failed to hydrate shared TC base details:", error);
      } finally {
        if (!cancelled) setIsSharedBaseHydrating(false);
      }
    };

    hydrateSharedBaseDetails();
    return () => {
      cancelled = true;
    };
  }, [TE_ID, currentTab, executeGet]);

  // Run background RMDF validation on mount/edit to show correct warnings/errors immediately.
  useEffect(() => {
    if (!TE_ID) return;

    let cancelled = false;

    const runBackgroundRmdfValidation = async () => {
      try {
        // 1. Fetch incoming TCs
        const incomingRes = await fetchIncomingTcDetailsList(executeGet, TE_ID, 100, 0);
        if (cancelled) return;
        const incomingEntries = Array.isArray(incomingRes?.entries) ? incomingRes.entries : [];

        // Check if there is at least one RMDF TC
        const hasRmdfIncoming = incomingEntries.some((item: any) => {
          const incomingTcInfo = item.incomingTcInfo || {};
          return Boolean(incomingTcInfo.tcIncomingRMDFNo || (incomingTcInfo as any).tcIncomingType === "RMDF");
        });

        if (!hasRmdfIncoming) {
          useTcTabErrorStore.getState().setTabError('product-details', false);
          useTcTabErrorStore.getState().setRmdfViolations({});
          return;
        }

        // 2. Fetch products list
        const productsRes: any = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
        if (cancelled) return;

        let responseArray: any[] = [];
        if (productsRes?.data?.response) {
          if (typeof productsRes.data.response === 'string') {
            try {
              responseArray = JSON.parse(productsRes.data.response);
            } catch (e) {
              responseArray = [];
            }
          } else if (Array.isArray(productsRes.data.response)) {
            responseArray = productsRes.data.response;
          }
        }

        if (responseArray.length === 0) return;

        // 3. Fetch detailed products
        const detailedProducts = await Promise.all(
          responseArray.map(async (p: any) => {
            try {
              const res = await fetchProductaddDetails(executeGet, TE_ID, p.productId, p.shipmentId);
              const prod = res?.data?.tcProductInformation?.[0] || res?.data || {};
              return { ...p, ...prod };
            } catch (err) {
              return p;
            }
          })
        );

        if (cancelled) return;

        // 4. Compute violations
        const violations: Record<string, { missingFeedstock: boolean; missingReclaimedSource: boolean }> = {};
        incomingEntries.forEach((item: any) => {
          const incomingTcInfo = item.incomingTcInfo || {};
          const isRMDF = Boolean(incomingTcInfo.tcIncomingRMDFNo || (incomingTcInfo as any).tcIncomingType === "RMDF");
          if (!isRMDF) return;

          const articleNo = incomingTcInfo.tcAssociatedProductArticleNo;
          if (!articleNo) return;

          const product = detailedProducts.find((p) => String((p as any).tcProductArticleNo) === String(articleNo)) as any;
          if (!product) return;

          const feedstock = product.tcProductFeedstock;
          const reclaimedSource = product.tcProductReclaimedMaterialSource;

          const missingFeedstock = !feedstock || (Array.isArray(feedstock) ? feedstock.length === 0 : String(feedstock).trim() === "");
          const missingReclaimedSource = !reclaimedSource || (Array.isArray(reclaimedSource) ? reclaimedSource.length === 0 : String(reclaimedSource).trim() === "");

          if (missingFeedstock || missingReclaimedSource) {
            violations[articleNo] = {
              missingFeedstock,
              missingReclaimedSource,
            };
          }
        });

        const hasError = Object.keys(violations).length > 0;
        useTcTabErrorStore.getState().setTabError('product-details', hasError);
        useTcTabErrorStore.getState().setRmdfViolations(violations);
      } catch (err) {
        console.error("Failed background validation check:", err);
      }
    };

    runBackgroundRmdfValidation();

    return () => {
      cancelled = true;
    };
  }, [TE_ID, executeGet]);

  // Clear all tab error flags when the user navigates to a different TC (TE_ID changes).
  // Ensures orange pill indicators from a previous TC never bleed into a new session.
  useEffect(() => {
    clearTabErrors();
  }, [TE_ID]);

  // Order must match API keys and UX sequence exactly
  // Use the same IDs used by the child tab components (v4 routes)
  const mandatoryTabs = [
    "base-details", // transaction-certificate
    "buyer-seller-products",
    "shipment-details",
    "product-details",
    "incoming-tc-details",
    "certified-standard-weight",
    "declaration-submit-details",
  ];

  const fetchCompletionData = async () => {
    if (!TE_ID) return {};
    try {
      const response: any = await executeGet(TCAppConfig.GET_DISABLE_V4_TABS, {
        tcId: TE_ID,
      });

      const map = response.data || {};
      setCompletionMap(map);

      if (map["tc-status"]) {
        setTcStatus(map["tc-status"]);
      }
      return map;
    } catch (error) {
      console.error("Error fetching TC tab completion status:", error);
      return {};
    }
  };

  useEffect(() => {
    if (enableDisableLogic) fetchCompletionData();
  }, [TE_ID, enableDisableLogic]);

  const handleNextTab = async () => {
    await fetchCompletionData();
  };

  const isTabCompleted = (tabId: string) => !!completionMap[tabId];

  const isReadOnlyBasedOnStatus = () =>
    tcStatus === "S" || tcStatus === "W" || tcStatus === "E";

  const baseTabs = [
    {
      id: "base-details",
      label: "Transaction Information",
      component: <TransactionCertificate onNextTab={handleNextTab} />,
    },
    {
      id: "buyer-seller-products",
      label: "Buyer and Seller Information",
      component: <SellerCertifiedProducts onNextTab={handleNextTab} />,
    },
    {
      id: "shipment-details",
      label: "Shipment Information",
      component: <Shipments onNextTab={handleNextTab} />,
    },
    {
      id: "product-details",
      label: "Product Information",
      component: <CertifiedProductsComponents onNextTab={handleNextTab} />,
    },
    // Component Information tab merged into Product Information (product-without-component).
    {
      id: "incoming-tc-details",
      label: "Incoming TC Information",
      component: <IncomingTcDetails onNextTab={handleNextTab} />,
    },
    {
      id: "certified-standard-weight",
      label: "Claims Category Certified Weight Information",
      component: <TcStandardWeightInfo onNextTab={handleNextTab} />,
    },
    {
      id: "declaration-submit-details",
      label: "Declarations",
      component: (
        <DeclarationByCertificateBody
          onNextTab={handleNextTab}
          completionMap={completionMap}
        />
      ),
    },
  ];

  const hasTE_ID = Boolean(TE_ID);

  const tabs = baseTabs.map((tab) => {
    // Determine whether this tab has a cross-tab error (e.g. RMDF violations on
    // "incoming-tc-details"). This flag causes the pill to render in orange.
    const hasTabError = Boolean(tabErrors[tab.id]);

    // When disable logic isn't used (submitted TC), enable all but still carry warning
    if (!enableDisableLogic) return { ...tab, disabled: false, warning: hasTabError };

    // If no TE_ID exists yet, only base-details is enabled
    if (!hasTE_ID) return { ...tab, disabled: tab.id !== "base-details", warning: hasTabError };

    // base-details always enabled
    if (tab.id === "base-details") return { ...tab, disabled: false, warning: hasTabError };

    // For other tabs, enable only if previous mandatory tab completed
    const idx = mandatoryTabs.indexOf(tab.id);
    if (idx > 0) {
      const prev = mandatoryTabs[idx - 1];
      if (!isTabCompleted(prev)) return { ...tab, disabled: true, warning: hasTabError };
    }

    // If any earlier mandatory tab has an active error (e.g. RMDF violations on
    // 'product-details'), lock all subsequent tabs — mirrors the SC v4 behaviour
    // where a failing facility-details tab disables product-category-information etc.
    if (idx > 0) {
      for (let i = 0; i < idx; i++) {
        const prevTabId = mandatoryTabs[i];
        if (tabErrors[prevTabId]) {
          return { ...tab, disabled: true, warning: hasTabError };
        }
      }
    }

    return { ...tab, disabled: false, warning: hasTabError };
  });

  return (
    <Box sx={{ mt: "2px" }}>
      {isSharedBaseHydrating ? (
        <LoaderComponent />
      ) : (
        <CustomizedTabs tabs={tabs} baseUrl={baseUrl} system="v4/tc" ID={TE_ID} />
      )}
    </Box>
  );
};

export default TcForm;
